import { useCallback, useEffect, useRef, useState } from "react";

import { OrganizationsAdminService, readGlobalFeatures } from "../services/organizationsAdmin.service";
import type {
  GlobalFeature,
  GlobalFeatureMode,
  GlobalFeatures,
  UpdateGlobalFeaturesPayload,
} from "../types/organizationsAdmin.types";
import type { FeatureToggleResult } from "./useOrganizationsAdminList";

export interface UseGlobalFeatures {
  features: GlobalFeatures | null;
  hasError: boolean;
  /** The switch being written. */
  savingFeature: GlobalFeature | null;
  /** Any switch is being written: every switch waits, so two writes never race. */
  isBusy: boolean;
  reload: () => void;
  /** `failed` too when the backend answered another mode than the one asked (someone moved it meanwhile). */
  setFeature: (feature: GlobalFeature, mode: GlobalFeatureMode) => Promise<FeatureToggleResult>;
}

/** Runs tasks one after another, whatever each one's outcome. */
export function createSerialQueue() {
  let tail: Promise<unknown> = Promise.resolve();
  return <T>(task: () => Promise<T>): Promise<T> => {
    const run = tail.then(task, task);
    tail = run.catch(() => undefined);
    return run;
  };
}

/** A write's answer settles only the switch it wrote: the other one may have changed meanwhile. */
export function settleFeature(
  previous: GlobalFeatures | null,
  answer: GlobalFeatures,
  feature: GlobalFeature,
): GlobalFeatures {
  return { ...(previous ?? answer), [feature]: answer[feature] };
}

/**
 * A move into or out of off for all goes as `modes`, the only way to set or lift a block. Every other move
 * keeps the boolean, which a backend that predates modes also takes.
 */
export function globalFeaturePayload(
  feature: GlobalFeature,
  mode: GlobalFeatureMode,
  current: GlobalFeatureMode | undefined,
): UpdateGlobalFeaturesPayload {
  if (mode === "off_for_all" || current === "off_for_all") return { modes: { [feature]: mode } };
  return { [feature]: mode === "on_for_all" };
}

export function useGlobalFeatures(): UseGlobalFeatures {
  const [features, setFeatures] = useState<GlobalFeatures | null>(null);
  const [hasError, setHasError] = useState(false);
  const [savingFeature, setSavingFeature] = useState<GlobalFeature | null>(null);
  const [pending, setPending] = useState(0);
  const [reloadToken, setReloadToken] = useState(0);
  const enqueue = useRef(createSerialQueue()).current;
  // What the last read or write left: writes run one after another, so each one decides its payload from it.
  const latest = useRef<GlobalFeatures | null>(null);

  useEffect(() => {
    let cancelled = false;
    OrganizationsAdminService.getGlobalFeatures()
      .then(({ data }) => {
        if (cancelled) return;
        latest.current = readGlobalFeatures(data);
        setFeatures(latest.current);
        setHasError(false);
      })
      .catch(() => {
        if (!cancelled) setHasError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  const setFeature = useCallback(
    (feature: GlobalFeature, mode: GlobalFeatureMode): Promise<FeatureToggleResult> => {
      setPending((count) => count + 1);
      return enqueue(async (): Promise<FeatureToggleResult> => {
        setSavingFeature(feature);
        try {
          const payload = globalFeaturePayload(feature, mode, latest.current?.[feature]);
          const { data } = await OrganizationsAdminService.updateGlobalFeatures(payload);
          const answer = readGlobalFeatures(data);
          latest.current = settleFeature(latest.current, answer, feature);
          setFeatures(latest.current);
          return answer[feature] === mode ? "ok" : "failed";
        } catch {
          return "failed";
        } finally {
          setSavingFeature(null);
          setPending((count) => count - 1);
        }
      });
    },
    [enqueue],
  );

  return { features, hasError, savingFeature, isBusy: pending > 0, reload, setFeature };
}
